import { LitElement, html, svg, css } from 'lit'
import { StoreMixin } from '../store.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Reporting & analytics dashboard (§6.9, §16). Agent-only (route guard). A date-range
// bar drives four aggregate endpoints; the results render as headline stat tiles, a
// created-vs-solved line chart, an open-by-status bar chart, and per-agent /
// per-client tables. Everything is aggregate — no per-ticket or note detail.
//
// Charts are hand-rolled inline SVG (the CSP forbids CDN chart libs) on the
// BrandExtract palette. The two-series volume colors were validated for CVD
// separation + surface contrast (dataviz skill); identity is carried by a legend +
// direct end-labels, never color alone, and every status bar is labeled.

// Created vs. solved — a CVD-safe, surface-contrasting two-hue pair (validated).
const C_CREATED = '#2f6fd0'
const C_SOLVED = '#4e8a2b'

// Status marks reuse the reserved ticket-status tokens, always paired with a label.
const STATUS_ORDER = ['new', 'open', 'pending', 'solved', 'closed']
const STATUS_COLOR = {
  new: 'var(--bx-status-new)', open: 'var(--bx-status-open)', pending: 'var(--bx-status-pending)',
  solved: 'var(--bx-status-solved)', closed: 'var(--bx-status-closed)'
}

function fmtHours(h) {
  if (h === null || h === undefined) return '—'
  if (h < 1) return `${Math.round(h * 60)} min`
  if (h >= 48) return `${(h / 24).toFixed(1)} d`
  return `${h} h`
}

class PageReports extends StoreMixin(LitElement) {
  static properties = {
    _from: { state: true },
    _to: { state: true },
    _interval: { state: true },
    _overview: { state: true },
    _agents: { state: true },
    _clients: { state: true },
    _volume: { state: true },
    _loading: { state: true },
    _hover: { state: true }   // { i, x, y } for the volume crosshair tooltip
  }

  static styles = css`
    :host { display: block; }
    h1 { color: var(--bx-brand); margin: 0 0 0.25rem; font-size: 1.4rem; }
    p.lede { color: var(--bx-muted); margin: 0 0 1rem; }

    .controls { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: flex-end; margin-bottom: 1.1rem; }
    .ctl { display: flex; flex-direction: column; gap: 0.25rem; }
    .ctl label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bx-muted); }
    input, select { font: inherit; padding: 0.4rem 0.5rem; border: 1px solid var(--bx-border);
                    border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); }
    .presets { display: flex; gap: 0.3rem; }
    button { font: inherit; cursor: pointer; padding: 0.4rem 0.7rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.82rem; }
    button:hover { background: var(--bx-bg); }

    .tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr)); gap: 0.8rem; margin-bottom: 1.2rem; }
    .tile { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); padding: 0.9rem 1rem; }
    .tile .k { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bx-muted); }
    .tile .v { font-size: 1.7rem; font-weight: 700; color: var(--bx-ink); line-height: 1.2; margin-top: 0.15rem; }
    .tile .sub { font-size: 0.75rem; color: var(--bx-muted); margin-top: 0.1rem; }

    .grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 1rem; align-items: start; }
    @media (max-width: 860px) { .grid { grid-template-columns: 1fr; } }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); padding: 1rem 1.1rem; margin-bottom: 1rem; }
    .card h2 { margin: 0 0 0.8rem; font-size: 1rem; color: var(--bx-ink); }

    .legend { display: flex; gap: 1rem; margin-bottom: 0.4rem; font-size: 0.8rem; color: var(--bx-muted); }
    .legend span { display: inline-flex; align-items: center; gap: 0.35rem; }
    .swatch { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }

    svg { width: 100%; height: auto; display: block; overflow: visible; }
    .grid-line { stroke: var(--bx-border); stroke-width: 1; }
    .axis-label { fill: var(--bx-muted); font-size: 10px; }
    .tip { position: absolute; pointer-events: none; background: var(--bx-brand); color: var(--bx-brand-ink);
           padding: 0.4rem 0.55rem; border-radius: 6px; font-size: 0.72rem; white-space: nowrap; z-index: 5;
           box-shadow: var(--bx-shadow); transform: translate(-50%, -120%); }
    .chart-wrap { position: relative; }

    .bars { display: flex; flex-direction: column; gap: 0.5rem; }
    .bar-row { display: grid; grid-template-columns: 4.5rem 1fr 2rem; align-items: center; gap: 0.5rem; font-size: 0.85rem; }
    .bar-row .name { color: var(--bx-ink); text-transform: capitalize; }
    .bar-track { background: var(--bx-bg); border-radius: 5px; height: 14px; overflow: hidden; }
    .bar-fill { height: 100%; border-radius: 5px; }
    .bar-row .n { text-align: right; color: var(--bx-muted); font-variant-numeric: tabular-nums; }

    table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
    th, td { text-align: left; padding: 0.45rem 0.5rem; border-bottom: 1px solid var(--bx-border); }
    th { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bx-muted); font-weight: 600; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .mix { display: flex; height: 12px; border-radius: 4px; overflow: hidden; min-width: 6rem; background: var(--bx-bg); }
    .mix span { height: 100%; }
    .empty, .loading { padding: 2.5rem; text-align: center; color: var(--bx-muted); }
    .footnote { font-size: 0.72rem; color: var(--bx-muted); margin-top: 0.3rem; }
  `

  constructor() {
    super()
    const today = new Date().toISOString().slice(0, 10)
    const from = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10)
    this._from = from
    this._to = today
    this._interval = 'day'
    this._overview = null
    this._agents = []
    this._clients = []
    this._volume = null
    this._loading = true
    this._hover = null
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  _qs() {
    return `?from=${this._from}&to=${this._to}`
  }

  async _load() {
    this._loading = true
    try {
      const [overview, byAgent, byClient, volume] = await Promise.all([
        api.get(`/reports/overview${this._qs()}`),
        api.get(`/reports/by-agent${this._qs()}`),
        api.get(`/reports/by-client${this._qs()}`),
        api.get(`/reports/volume${this._qs()}&interval=${this._interval}`)
      ])
      this._overview = overview
      this._agents = byAgent.agents
      this._clients = byClient.clients
      this._volume = volume
    } catch (err) {
      toast(err.message || 'Failed to load reports', 'error')
    } finally {
      this._loading = false
    }
  }

  _preset(days) {
    this._to = new Date().toISOString().slice(0, 10)
    this._from = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10)
    this._load()
  }

  // --- volume line chart ----------------------------------------------------
  _renderVolume() {
    const series = this._volume?.series || []
    if (!series.length) return html`<div class="empty">No data in range.</div>`

    const W = 640, H = 220, PAD = { l: 34, r: 16, t: 12, b: 26 }
    const iw = W - PAD.l - PAD.r, ih = H - PAD.t - PAD.b
    const max = Math.max(1, ...series.map(s => Math.max(s.created, s.solved)))
    const n = series.length
    const x = i => PAD.l + (n === 1 ? iw / 2 : (i / (n - 1)) * iw)
    const y = v => PAD.t + ih - (v / max) * ih

    const line = key => series.map((s, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(s[key]).toFixed(1)}`).join(' ')
    // y grid: 0, mid, max
    const ticks = [0, Math.round(max / 2), max].filter((v, i, a) => a.indexOf(v) === i)
    const label = i => {
      const d = series[i].bucket.slice(5, 10)   // MM-DD
      return d
    }
    const step = Math.ceil(n / 6)

    return html`
      <div class="legend">
        <span><i class="swatch" style="background:${C_CREATED}"></i>Created</span>
        <span><i class="swatch" style="background:${C_SOLVED}"></i>Solved</span>
      </div>
      <div class="chart-wrap"
           @mousemove=${e => this._trackHover(e, series, x, y, W)}
           @mouseleave=${() => (this._hover = null)}>
        <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Created versus solved tickets over time">
          ${ticks.map(t => svg`
            <line class="grid-line" x1=${PAD.l} y1=${y(t)} x2=${W - PAD.r} y2=${y(t)} />
            <text class="axis-label" x=${PAD.l - 6} y=${y(t) + 3} text-anchor="end">${t}</text>`)}
          ${series.map((s, i) => (i % step === 0 || i === n - 1) ? svg`
            <text class="axis-label" x=${x(i)} y=${H - 8} text-anchor="middle">${label(i)}</text>` : '')}
          <path d=${line('created')} fill="none" stroke=${C_CREATED} stroke-width="2"
                stroke-linejoin="round" stroke-linecap="round" />
          <path d=${line('solved')} fill="none" stroke=${C_SOLVED} stroke-width="2"
                stroke-linejoin="round" stroke-linecap="round" />
          ${series.map((s, i) => svg`
            <circle cx=${x(i)} cy=${y(s.created)} r="3" fill=${C_CREATED} />
            <circle cx=${x(i)} cy=${y(s.solved)} r="3" fill=${C_SOLVED} />`)}
          ${this._hover ? svg`<line class="grid-line" x1=${this._hover.px} y1=${PAD.t} x2=${this._hover.px} y2=${PAD.t + ih} stroke-dasharray="3 3" />` : ''}
        </svg>
        ${this._hover ? html`
          <div class="tip" style="left:${(this._hover.px / W) * 100}%; top:${(this._hover.py / H) * 100}%">
            <div>${series[this._hover.i].bucket.slice(0, 10)}</div>
            <div>Created: ${series[this._hover.i].created} · Solved: ${series[this._hover.i].solved}</div>
          </div>` : ''}
      </div>`
  }

  _trackHover(e, series, x, y, W) {
    const rect = e.currentTarget.getBoundingClientRect()
    const relX = ((e.clientX - rect.left) / rect.width) * W
    let best = 0, bestD = Infinity
    for (let i = 0; i < series.length; i++) {
      const d = Math.abs(x(i) - relX)
      if (d < bestD) { bestD = d; best = i }
    }
    const s = series[best]
    this._hover = { i: best, px: x(best), py: y(Math.max(s.created, s.solved)) }
  }

  // --- open-by-status bars --------------------------------------------------
  _renderStatusBars() {
    const mix = this._overview?.openByStatus || {}
    const max = Math.max(1, ...STATUS_ORDER.map(s => mix[s] || 0))
    return html`
      <div class="bars">
        ${STATUS_ORDER.map(s => html`
          <div class="bar-row">
            <span class="name">${s}</span>
            <div class="bar-track">
              <div class="bar-fill" style="width:${((mix[s] || 0) / max) * 100}%; background:${STATUS_COLOR[s]}"></div>
            </div>
            <span class="n">${mix[s] || 0}</span>
          </div>`)}
      </div>`
  }

  _renderMix(m) {
    const total = STATUS_ORDER.reduce((a, s) => a + (m[s] || 0), 0) || 1
    return html`<div class="mix" title=${STATUS_ORDER.map(s => `${s}: ${m[s] || 0}`).join(', ')}>
      ${STATUS_ORDER.map(s => (m[s] ? html`<span style="width:${(m[s] / total) * 100}%; background:${STATUS_COLOR[s]}"></span>` : ''))}
    </div>`
  }

  render() {
    if (this._loading && !this._overview) return html`<div class="loading">Loading…</div>`
    const o = this._overview

    return html`
      <h1>Reports</h1>
      <p class="lede">Support metrics across the selected date range. Aggregate figures only.</p>

      <div class="controls">
        <div class="ctl">
          <label>From</label>
          <input type="date" .value=${this._from} max=${this._to}
                 @change=${e => { this._from = e.target.value; this._load() }} />
        </div>
        <div class="ctl">
          <label>To</label>
          <input type="date" .value=${this._to} min=${this._from}
                 @change=${e => { this._to = e.target.value; this._load() }} />
        </div>
        <div class="ctl">
          <label>Interval</label>
          <select .value=${this._interval} @change=${e => { this._interval = e.target.value; this._load() }}>
            <option value="day">Day</option>
            <option value="week">Week</option>
          </select>
        </div>
        <div class="presets">
          <button @click=${() => this._preset(7)}>7d</button>
          <button @click=${() => this._preset(30)}>30d</button>
          <button @click=${() => this._preset(90)}>90d</button>
        </div>
      </div>

      ${o ? html`
        <div class="tiles">
          <div class="tile"><div class="k">Created</div><div class="v">${o.created}</div><div class="sub">in range</div></div>
          <div class="tile"><div class="k">Solved</div><div class="v">${o.solved}</div><div class="sub">in range</div></div>
          <div class="tile"><div class="k">Backlog</div><div class="v">${o.backlog}</div><div class="sub">open · unassigned</div></div>
          <div class="tile"><div class="k">First response</div><div class="v">${fmtHours(o.firstResponse.avgHours)}</div><div class="sub">avg · median ${fmtHours(o.firstResponse.medianHours)}</div></div>
          <div class="tile"><div class="k">Resolution</div><div class="v">${fmtHours(o.resolution.avgHours)}</div><div class="sub">avg · median ${fmtHours(o.resolution.medianHours)}</div></div>
          <div class="tile"><div class="k">CSAT</div><div class="v">${o.csat.score === null ? '—' : o.csat.score + '%'}</div><div class="sub">${o.csat.good} good · ${o.csat.bad} bad</div></div>
        </div>

        <div class="grid">
          <div class="card">
            <h2>Volume — created vs. solved</h2>
            ${this._renderVolume()}
          </div>
          <div class="card">
            <h2>Open by status</h2>
            ${this._renderStatusBars()}
            <div class="footnote">Current snapshot across all active tickets.</div>
          </div>
        </div>

        <div class="card">
          <h2>By agent</h2>
          ${this._agents.length ? html`
            <table>
              <thead><tr><th>Agent</th><th class="num">Resolved</th><th class="num">Avg resolution</th><th class="num">CSAT</th></tr></thead>
              <tbody>
                ${this._agents.map(a => html`<tr>
                  <td>${a.name || a.email}</td>
                  <td class="num">${a.resolved}</td>
                  <td class="num">${fmtHours(a.avgResolutionHours)}</td>
                  <td class="num">${a.csat.score === null ? '—' : a.csat.score + '%'}</td>
                </tr>`)}
              </tbody>
            </table>` : html`<div class="empty">No resolved tickets in range.</div>`}
        </div>

        <div class="card">
          <h2>By client</h2>
          ${this._clients.length ? html`
            <table>
              <thead><tr><th>Client</th><th class="num">Volume</th><th>Status mix</th></tr></thead>
              <tbody>
                ${this._clients.map(c => html`<tr>
                  <td>${c.name}</td>
                  <td class="num">${c.volume}</td>
                  <td>${this._renderMix(c.statusMix)}</td>
                </tr>`)}
              </tbody>
            </table>` : html`<div class="empty">No tickets in range.</div>`}
        </div>
      ` : html`<div class="empty">No data.</div>`}
    `
  }
}

customElements.define('page-reports', PageReports)
